import classNames from 'classnames';
import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { SelectOption } from './useSelect';
import { ChevronDownIcon } from '@heroicons/react/24/outline';

export interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  options: SelectOption[];
  className?: string;
  optionClassName?: string;
  handleSelect: (option: any) => void;
  asButton?: boolean; // Render as a button dropdown instead of native select
  buttonLabel?: string; // Label for button mode
  buttonClassName?: string; // Custom className for button
}

const Select: React.FC<SelectProps> = ({
  options,
  optionClassName,
  className = '',
  asButton = false,
  buttonLabel,
  buttonClassName,
  handleSelect,
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });

  // Update dropdown position when it opens or on scroll/resize
  useEffect(() => {
    if (!isOpen || !buttonRef.current) return;

    const updatePosition = () => {
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        // Use viewport coordinates directly for fixed positioning
        setDropdownPosition({
          top: rect.bottom,
          left: rect.left,
          width: rect.width,
        });
      }
    };

    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!asButton) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [asButton]);

  // Render as button dropdown
  if (asButton) {
    const selectedOption = options.find(option => option.value === props.value);
    const displayLabel = buttonLabel || selectedOption?.label || 'Select...';

    return (
      <div className={classNames(className, 'inline-block')}>
        <div className="relative">
          <button
            ref={buttonRef}
            type="button"
            disabled={!options?.length || !!props.disabled}
            onClick={() => setIsOpen(!isOpen)}
            className={classNames(
              buttonClassName,
              'inline-flex items-center justify-between gap-2 px-4 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 enabled:hover:bg-gray-50 focus:outline-none focus:ring focus:ring-blue-500',
            )}
          >
            <span>{displayLabel}</span>
            <ChevronDownIcon className={classNames('h-4 w-4 transition-transform', isOpen && 'rotate-180')} />
          </button>

          {isOpen &&
            createPortal(
              <div
                ref={dropdownRef}
                className="fixed z-[99999] bg-white border border-gray-300 rounded-md shadow-xl max-h-60 overflow-auto"
                style={{
                  top: `${dropdownPosition.top}px`,
                  left: `${dropdownPosition.left}px`,
                  minWidth: `${dropdownPosition.width}px`,
                }}
              >
                {options.map(option => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => {
                      handleSelect(option);
                      setIsOpen(false);
                    }}
                    className={classNames(
                      'w-full text-left px-4 py-2 text-sm hover:bg-gray-100 focus:outline-none focus:bg-gray-100',
                      option.value === props.value && 'bg-blue-50 text-blue-700',
                    )}
                  >
                    {option.label}
                  </button>
                ))}
              </div>,
              document.body,
            )}
        </div>
      </div>
    );
  }

  // Render as native select
  return (
    <div className={classNames(className, 'inline-block relative h-fit')}>
      <select
        className={classNames(
          optionClassName,
          'pl-3 pr-10 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900',
        )}
        {...props}
        onChange={e => {
          const value = e.target.value;

          // eslint-disable-next-line eqeqeq
          const selectedOption = options.find(option => option.value == value);

          if (selectedOption) {
            handleSelect(selectedOption);
          }
        }}
      >
        {options.map(option => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
};

export default Select;
